import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { ContactLabelSelector } from '../components/ContactLabelSelector';
import { ContactCategorySelector } from '../components/ContactCategorySelector';

interface WhatsAppSession {
  name: string;
  displayName?: string;
  status: string;
  provider: string;
}

interface WAHAGroup {
  id?: string;
  subject?: string;
  name?: string;
  participants?: any[];
  participantCount?: number;
  ParticipantCount?: number;
  [key: string]: any;
}

interface ContactCategory {
  id: string;
  nome: string;
  cor?: string;
  descricao?: string | null;
}

export function GroupContactExtractorPage() {
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [groups, setGroups] = useState<WAHAGroup[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [contactCategories, setContactCategories] = useState<ContactCategory[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingContactCategories, setLoadingContactCategories] = useState(false);
  const [showExtractModal, setShowExtractModal] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractedParticipants, setExtractedParticipants] = useState<any[]>([]);
  const [selectedCategoryForSave, setSelectedCategoryForSave] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [savingParticipants, setSavingParticipants] = useState(false);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers
    });
  };

  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      const response = await authenticatedFetch('/api/waha/sessions');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      const workingSessions = data.filter((session: WhatsAppSession) => session.status === 'WORKING');
      setSessions(workingSessions);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
      toast.error('Erro ao carregar sessões WhatsApp');
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadContactCategories = async () => {
    try {
      setLoadingContactCategories(true);
      const response = await authenticatedFetch('/api/categorias/all');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setContactCategories(data);
    } catch (error) {
      console.error('Erro ao carregar categorias de contatos:', error);
      toast.error('Erro ao carregar categorias de contatos');
    } finally {
      setLoadingContactCategories(false);
    }
  };

  useEffect(() => {
    loadSessions();
    loadContactCategories();
  }, []);

  const loadGroups = async (sessionName: string) => {
    try {
      setLoadingGroups(true);
      const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}/groups`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setGroups(data);
      toast.success(`${data.length} grupo(s) encontrados`);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
      toast.error('Erro ao carregar grupos da sessão');
    } finally {
      setLoadingGroups(false);
    }
  };

  const getGroupId = (group: WAHAGroup) => group.id || (group as any).JID || (group as any).LID || (group as any).PhoneNumber || '';
  const getGroupName = (group: WAHAGroup) =>
    group.subject || (group as any).Subject || group.name || (group as any).Name || (group as any).DisplayName || getGroupId(group) || 'Sem nome';
  const getGroupParticipants = (group: WAHAGroup) => group.participants || (group as any).Participants || [];
  const getGroupParticipantCount = (group: WAHAGroup) => {
    const participants = getGroupParticipants(group);
    return group.participantCount ?? group.ParticipantCount ?? participants.length;
  };

  const handleSessionChange = (sessionName: string) => {
    setSelectedSession(sessionName);
    setGroups([]);
    setSelectedGroups([]);
    if (sessionName) {
      loadGroups(sessionName);
    }
  };

  const handleGroupToggle = (groupId: string) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const handleSelectAllGroups = () => {
    if (selectedGroups.length === groups.length) {
      setSelectedGroups([]);
    } else {
      const ids = groups
        .map((group) => getGroupId(group))
        .filter((id): id is string => Boolean(id));
      setSelectedGroups(ids);
    }
  };

  const handleExtractParticipants = async () => {
    try {
      setExtracting(true);
      
      const response = await authenticatedFetch('/api/group-participants/extract', {
        method: 'POST',
        body: JSON.stringify({
          groupIds: selectedGroups,
          sessionName: selectedSession
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setExtractedParticipants(data.results);
      setShowExtractModal(true);
      
      const totalParticipants = data.results.reduce((sum: number, group: any) => sum + group.total, 0);
      toast.success(`${totalParticipants} participantes extraídos de ${data.results.length} grupo(s)`);
    } catch (error) {
      console.error('Erro ao extrair participantes:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao extrair participantes');
    } finally {
      setExtracting(false);
    }
  };

  const handleSaveParticipants = async () => {
    try {
      if (!selectedCategoryForSave) {
        toast.error('Selecione uma categoria');
        return;
      }

      setSavingParticipants(true);

      const allParticipants = extractedParticipants.flatMap((group: any) =>
        group.participants.map((p: any) => ({
          phoneNumber: p.phoneNumber,
          displayName: p.displayName
        }))
      );

      const response = await authenticatedFetch('/api/group-participants/save', {
        method: 'POST',
        body: JSON.stringify({
          participants: allParticipants,
          categoryId: selectedCategoryForSave,
          tags: selectedTags
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      toast.success(
        `Contatos salvos! ${result.imported} novos, ${result.updated} atualizados, ${result.skipped} ignorados`
      );
      setShowExtractModal(false);
      setExtractedParticipants([]);
      setSelectedCategoryForSave('');
    } catch (error) {
      console.error('Erro ao salvar participantes:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar participantes');
    } finally {
      setSavingParticipants(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        title="Extrator de Contatos de Grupos"
        subtitle="Extraia contatos dos participantes de grupos do WhatsApp e salve em categorias"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Extrator de Contatos de Grupos</h1>
          <p className="mt-2 text-sm text-gray-600">
            Selecione uma sessão WhatsApp, escolha os grupos e extraia os participantes para salvar como contatos.
          </p>
        </div>

        {/* Sessão WhatsApp */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">1. Selecione a Sessão WhatsApp</h2>
          {loadingSessions ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-4 text-gray-500">Nenhuma sessão ativa encontrada</div>
          ) : (
            <select
              value={selectedSession}
              onChange={(event) => handleSessionChange(event.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Selecione uma sessão...</option>
              {sessions.map((session) => (
                <option key={session.name} value={session.name}>
                  {session.displayName || session.name} ({session.provider === 'WAHA' ? 'WhatsApp Não Oficial' : session.provider})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Grupos */}
        {selectedSession && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">2. Selecione os Grupos</h2>
                <p className="text-sm text-gray-500">Selecione os grupos dos quais deseja extrair os participantes.</p>
              </div>
              <div className="flex items-center gap-3">
                {groups.length > 0 && (
                  <button
                    type="button"
                    onClick={handleSelectAllGroups}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {selectedGroups.length === groups.length ? 'Desmarcar todos' : 'Selecionar todos'}
                  </button>
                )}
              </div>
            </div>
            {loadingGroups ? (
              <p className="mt-4 text-gray-600">Carregando grupos...</p>
            ) : groups.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Nenhum grupo encontrado nesta sessão</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groups.map((group) => {
                  const groupId = getGroupId(group);
                  if (!groupId) return null;

                  const groupName = getGroupName(group);
                  const participantsCount = getGroupParticipantCount(group);

                  return (
                    <label
                      key={groupId}
                      className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedGroups.includes(groupId)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedGroups.includes(groupId)}
                        onChange={() => handleGroupToggle(groupId)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="font-medium text-gray-900">{groupName}</div>
                        {participantsCount > 0 && (
                          <div className="text-sm text-gray-500">{participantsCount} participantes</div>
                        )}
                        <div className="text-xs text-gray-400 mt-1 truncate">{groupId}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {selectedGroups.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>{selectedGroups.length}</strong> grupo(s) selecionado(s)
                </p>
              </div>
            )}
          </div>
        )}

        {/* Botão Extrair */}
        {selectedSession && selectedGroups.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">3. Extrair Participantes</h2>
            <p className="text-sm text-gray-600 mb-4">
              Clique no botão abaixo para extrair os participantes dos grupos selecionados. Depois você poderá salvar os contatos em uma categoria.
            </p>
            <button
              type="button"
              onClick={handleExtractParticipants}
              className="w-full md:w-auto px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              disabled={extracting || selectedGroups.length === 0}
            >
              {extracting ? 'Extraindo...' : `Extrair Participantes de ${selectedGroups.length} grupo(s)`}
            </button>
          </div>
        )}
      </div>

      {/* Modal de Resultados */}
      {showExtractModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Participantes Extraídos</h3>
                <p className="text-sm text-gray-500">
                  {extractedParticipants.reduce((sum: number, g: any) => sum + g.total, 0)} participantes de{' '}
                  {extractedParticipants.length} grupo(s)
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowExtractModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {extractedParticipants.map((group: any, idx: number) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">
                    {group.groupName} ({group.total} participantes)
                  </h4>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {group.participants.map((p: any, pIdx: number) => (
                      <div key={pIdx} className="text-sm text-gray-600 flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-400">{p.phoneNumber}</span>
                        {p.displayName && <span>- {p.displayName}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="border-t pt-4">
                {loadingContactCategories ? (
                  <p className="text-sm text-gray-500">Carregando categorias...</p>
                ) : (
                  <ContactCategorySelector categories={contactCategories} value={selectedCategoryForSave} onChange={setSelectedCategoryForSave} label="Salvar em qual categoria?" optional={false} />
                )}
              </div>
              <ContactLabelSelector value={selectedTags} onChange={setSelectedTags} />
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <button
                type="button"
                onClick={() => setShowExtractModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveParticipants}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60"
                disabled={savingParticipants || !selectedCategoryForSave}
              >
                {savingParticipants ? 'Salvando...' : 'Salvar Contatos'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



